import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of, lastValueFrom, throwError, tap, firstValueFrom } from 'rxjs';
import { ConfigService } from '../config/config.service';
import { jwtDecode } from "jwt-decode";
import { ChangeThemeService } from '../cambio-tema/change-theme.service';
import { ModalService } from '../modal/modal.service';
import { Router } from '@angular/router';
import { UserUpdatePassword } from '../../shared/interfaces/usuarios/users.model';
import { MenuService } from '../menu/menu.service';

export interface UserData {
  idUser: number;
  userName: string;
  isPatient: boolean;
  roleName: string;
  idRole: number;
  idPatient: number
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  principalData: any = null;
  currentTheme: string = 'light-theme';
  private accessToken: string = '';
  private userData: UserData | null = null;

  constructor(private http: HttpClient,
    private cs: ConfigService,
    private modalService: ModalService,
    private router: Router,
    private menuservice: MenuService) {
  }


  /**
 * Realiza el proceso de valicación del usuario, si hay una sesión abierta cierra las otras sesiones a traves de activación de sockeet
 *
 * - Envía las credenciales al endpoint de autenticación.
 * - Si la respuesta es exitosa:
 *   - Limpia el almacenamiento de sesión previo.
 *   - Guarda el access token y el refresh token.
 *   - Obtiene el menú del usuario autenticado.
 *
 * @param userLogin Credenciales del usuario (ej: usuario y contraseña).
 * @returns {Observable<any>} Respuesta del servidor con el estado del login y los datos recibidos.
 */
  validateCredentialsAccess(userLogin: any): Observable<any> {
    return this.http.put(`${this.cs.urlEnviroment}api/Login/api/validate/credentials/access `, userLogin).pipe(
      map((resp: any) => {
        if (resp.ok) {
          this.setAcessToken(resp.data)
        }
        return resp;
      })
    );
  }

  /**
   * Realiza el proceso de inicio de sesión del usuario.
   *
   * - Envía las credenciales al endpoint de autenticación.
   * - Si la respuesta es exitosa:
   *   - Limpia el almacenamiento de sesión previo.
   *   - Guarda el access token y el refresh token.
   *   - Obtiene el menú del usuario autenticado.
   *
   * @param userLogin Credenciales del usuario (ej: usuario y contraseña).
   * @returns {Observable<any>} Respuesta del servidor con el estado del login y los datos recibidos.
   */
  login(userLogin: any): Observable<any> {
    return this.http.post(`${this.cs.urlEnviroment}api/login`, userLogin, { withCredentials: true }).pipe(
      map((resp: any) => {
        if (resp.ok) {
          this.setAcessToken(resp.data)
        }
        return resp;
      })
    );
  }

  private setUserData(data: UserData): void {
    this.userData = data;
  }

  readUserData(): UserData | null {
    return this.userData ? { ...this.userData } : null;
  }
  deleteUserInfo(): UserData | null {
    return this.userData = null;
  }


  /** Funcionalidad para cerrar sesón*/
  async logout(isLogoutInBackend: boolean, showModalExpired: boolean, showModalInactive: boolean = false): Promise<void> {
    try {

      const username = this.userData?.userName;
      if (isLogoutInBackend) {
        await lastValueFrom(this.logoutService());
      }
      const remember = sessionStorage.getItem('rememberUser');
      sessionStorage.clear();
      if (username && remember) {
        sessionStorage.setItem('username', String(username));
        sessionStorage.setItem('rememberUser', String(remember));
      }

    } catch (error) {
      sessionStorage.clear();
    }
    this.router.navigate(['login']);
    this.deleteAccessToken();
    this.deleteUserInfo();
    this.menuservice.deleteMenu();
    if (showModalExpired) this.modalService.openStatusMessage('Aceptar', 'Su sesión ha expirado. Por favor, inicie sesión nuevamente para continuar.', '3');
    if (showModalInactive) this.modalService.openStatusMessage('Aceptar', '¡Su sesión se ha cerrado por más de 20 minutos de inactividad!', '3')
  }

  logoutService(): Observable<any> {
    return this.http.put(`${this.cs.urlEnviroment}api/login/logout`,
      {}, // body vacío
      { withCredentials: true } // opciones reales
    );
  }


  //Envio de correo para el codigo
  sendEmailCode(objetoCorreo: any): Observable<any> {
    return this.http.post(`${this.cs.urlEnviroment}api/User/email`, objetoCorreo)
  }
  //Eliminar codigo para que no funcione mas
  deleteCode(idUser: any): Observable<any> {
    return this.http.get(`${this.cs.urlEnviroment}api/User/Confirm/${idUser}`)
  }
  //Confirmar codigo para cambiar contraseña
  confirmCode(body: any): Observable<any> {
    return this.http.post(`${this.cs.urlEnviroment}api/User/Confirm`, body)
  }

  //Cambiar contraseña
  updatePassword(objPass: UserUpdatePassword): Observable<any> {
    return this.http.post(`${this.cs.urlEnviroment}api/User/updatePass`, objPass)
  }
  //Establecer access token en la variable en memoria;
  setAcessToken(token: any): void {
    this.accessToken = token;
  }
  //Devuelve access token, si no lo encuentra devuelve null
  getAccessToken(): string | null {
    return this.accessToken;
  }
  deleteAccessToken(): void {
    this.accessToken = '';
  }

  /**
   * Decodifica el access token almacenado en la aplicación.
   *
   * - Si existe un token válido, retorna el objeto con la información decodificada.
   * - Si no hay token o la decodificación falla, retorna `null`.
   *
   * @returns {any | null} Datos del token decodificado o `null` si no es válido.
   */


  //Establecer access token his en el session storage;
  setTokenHis(tokenHis: any): void {
    sessionStorage.setItem('tokenHis', tokenHis);
  }

  getTokenHis() {
    return sessionStorage.getItem('tokenHis');
  }

  //Metodo para refrescar tokens
  refreshTokenService(): Observable<any> {
    return this.http.post(
      `${this.cs.urlEnviroment}api/Login/refreshToken`,
      { withCredentials: true }
    );
  }

  async loadUser(): Promise<any> {
    return firstValueFrom(this.ValidateSession())
      .then(session => session?.ok && session.data)
      .catch(() => false);
  }

  ValidateSession(): Observable<any> {
    return this.http.get<any>(
      `${this.cs.urlEnviroment}api/Login/validateSessionActive`,
      { withCredentials: true }
    ).pipe(
      tap(session => {
        if (session.ok && session.data) {

          const dataUser: UserData = {
            idUser: session.data.idUser,
            userName: session.data.userName,
            isPatient: false,
            roleName: session.data.roleName,
            idRole: session.data.idRole,
            idPatient: 0
          };

          this.setUserData(dataUser);
        }
      })
    );
  }








}
